import { supabase } from './supabase'

export async function getProductos({ incluirEliminados = false } = {}) {
  let q = supabase
    .from('productos')
    .select(`
      id, sku, nombre, marca, presentacion,
      stock_actual, stock_minimo, stock_critico,
      costo_unitario, precio_publico, activo, pausado, publicado, categoria, tier, vendible_suelto,
      imagen_url, deleted_at,
      descripcion, beneficios, ficha_actualizada_at,
      proveedor:proveedores(id, nombre),
      ficha_por:profiles!productos_ficha_actualizada_por_fkey(id, nombre)
    `)
    .order('sku')
  if (!incluirEliminados) q = q.is('deleted_at', null).eq('activo', true)
  const { data, error } = await q
  if (error) { console.error(error); return [] }
  return data || []
}

/** Devuelve true si la ficha tienda del producto está cargada con lo mínimo:
 *  descripción no nula y al menos un beneficio. */
export function fichaCargadaOK(producto) {
  if (!producto) return false
  const tieneDesc = !!(producto.descripcion && producto.descripcion.trim().length > 5)
  const benef = producto.beneficios
  const tieneBenef = Array.isArray(benef)
    ? benef.length > 0
    : (benef && typeof benef === 'object' && Object.keys(benef).length > 0)
  return tieneDesc && tieneBenef
}

export async function getProducto(sku) {
  const { data, error } = await supabase
    .from('productos')
    .select(`
      id, sku, nombre, marca, presentacion,
      stock_actual, stock_minimo, stock_critico,
      costo_unitario, iva_pct, precio_publico, precio_mercado, categoria, tier, vendible_suelto,
      activo, notas, proveedor_id,
      pausado, pausado_motivo, pausado_at, publicado, deleted_at,
      imagen_url, etiquetas_urls, descripcion, aroma, rendimiento, beneficios, modo_uso, ideal_para, composicion,
      grupo_equivalencia,
      proveedor:proveedores(id, nombre)
    `)
    .eq('sku', sku)
    .single()
  if (error) { console.error(error); return null }
  return data
}

export async function getUsoEnKits(productoId) {
  const { data, error } = await supabase
    .from('kit_items')
    .select('cantidad, kit:kits(codigo, nombre, tier, precio_susc)')
    .eq('producto_id', productoId)
  if (error) return []
  return data || []
}

// Lista todos los kits activos para el dropdown del editor
export async function getKitsDisponibles() {
  const { data, error } = await supabase
    .from('kits')
    .select('codigo, nombre, tier')
    .eq('activo', true)
    .order('tier').order('orden').order('nombre')
  if (error) { console.error('getKitsDisponibles:', error); return [] }
  return data || []
}

// Agregar un producto a un kit (o actualizar cantidad/es_regalo si ya existe)
// es_regalo: true → el producto va incluido en el kit pero no se cobra (afecta costo, no precio)
export async function upsertProductoEnKit(productoId, kitCodigo, cantidad, esRegalo = false) {
  const { error } = await supabase
    .from('kit_items')
    .upsert(
      { producto_id: productoId, kit_codigo: kitCodigo, cantidad: Number(cantidad), es_regalo: !!esRegalo },
      { onConflict: 'kit_codigo,producto_id' }
    )
  if (error) { console.error('upsertProductoEnKit:', error); throw error }
}

// Quitar un producto de un kit
export async function quitarProductoDeKit(productoId, kitCodigo) {
  const { error } = await supabase
    .from('kit_items')
    .delete()
    .eq('producto_id', productoId)
    .eq('kit_codigo', kitCodigo)
  if (error) { console.error('quitarProductoDeKit:', error); throw error }
}

// Lista distintos grupos de equivalencia ya creados (para autocomplete)
export async function getGruposEquivalencia() {
  const { data, error } = await supabase
    .from('productos')
    .select('grupo_equivalencia')
    .not('grupo_equivalencia', 'is', null)
    .is('deleted_at', null)
  if (error) return []
  const grupos = [...new Set((data || []).map(p => p.grupo_equivalencia))]
  return grupos.sort()
}

// Lista los productos de un grupo (para mostrar quiénes son los "hermanos")
export async function getMiembrosGrupo(grupoEquivalencia) {
  if (!grupoEquivalencia) return []
  const { data, error } = await supabase
    .from('productos')
    .select('id, sku, nombre, presentacion, marca, stock_actual')
    .eq('grupo_equivalencia', grupoEquivalencia)
    .is('deleted_at', null)
    .order('sku')
  if (error) return []
  return data || []
}

// Asigna o quita el grupo_equivalencia
export async function setGrupoEquivalencia(productoId, grupo) {
  const { error } = await supabase
    .from('productos')
    .update({ grupo_equivalencia: grupo || null })
    .eq('id', productoId)
  if (error) { console.error('setGrupoEquivalencia:', error); throw error }
}

export async function getDemandaProducto(productoId, dias = 30) {
  // Cuántas unidades se necesitarán en los próximos N días (por entregas programadas)
  const hasta = new Date()
  hasta.setDate(hasta.getDate() + dias)
  const { data, error } = await supabase.rpc('demanda_producto', {
    producto_id_param: productoId,
    dias_param: dias
  })
  if (error) return null
  return data?.[0] || null
}

export async function getKitsQueUsan(productoId) {
  const { data, error } = await supabase.rpc('kits_que_usan_producto', { p_producto_id: productoId })
  if (error) { console.error('getKitsQueUsan:', error); return [] }
  return data || []
}

export async function pausarProducto(productoId, pausar, motivo = null) {
  const { data, error } = await supabase.rpc('admin_pausar_producto', {
    p_id: productoId, p_pausar: pausar, p_motivo: motivo,
  })
  if (error) throw error
  return data
}

export async function crearProducto(datos) {
  const { data, error } = await supabase.rpc('admin_crear_producto', {
    p_sku: datos.sku,
    p_nombre: datos.nombre,
    p_categoria: datos.categoria,
    p_tier: datos.tier || 'C',
    p_marca: datos.marca || null,
    p_presentacion: datos.presentacion || null,
    p_costo_unitario: datos.costo_unitario || 0,
    p_precio_publico: datos.precio_publico || null,
    p_stock_minimo: datos.stock_minimo || 0,
    p_stock_critico: datos.stock_critico || 0,
    p_proveedor_id: datos.proveedor_id || null,
    p_vendible_suelto: datos.vendible_suelto !== false,
    p_iva_pct: datos.iva_pct || 22,
  })
  if (error) throw error

  // Si vinieron campos de ficha tienda, persistirlos en una segunda llamada
  const tieneFicha = datos.descripcion || datos.aroma || datos.rendimiento ||
                     (Array.isArray(datos.beneficios) && datos.beneficios.length > 0) ||
                     datos.modo_uso || datos.ideal_para || datos.composicion
  if (tieneFicha && data?.id) {
    try {
      await supabase.rpc('admin_actualizar_ficha_producto', {
        p_id: data.id,
        p_descripcion: datos.descripcion ?? '',
        p_aroma: datos.aroma ?? '',
        p_rendimiento: datos.rendimiento ?? '',
        p_beneficios: Array.isArray(datos.beneficios) ? datos.beneficios : [],
        p_modo_uso: datos.modo_uso ?? '',
        p_ideal_para: datos.ideal_para ?? '',
        p_composicion: datos.composicion ?? '',
      })
    } catch (e) {
      console.error('Producto creado, pero error guardando ficha:', e)
      // No tiramos error: el producto SÍ se creó. La ficha la puede editar el user a mano.
    }
  }
  return data
}

export async function editarProducto(id, datos) {
  const { data, error } = await supabase.rpc('admin_editar_producto', {
    p_id: id,
    p_sku: datos.sku,
    p_nombre: datos.nombre,
    p_categoria: datos.categoria,
    p_tier: datos.tier || 'C',
    p_marca: datos.marca ?? null,
    p_presentacion: datos.presentacion ?? null,
    p_costo_unitario: datos.costo_unitario ?? null,
    p_precio_publico: datos.precio_publico ?? null,
    p_precio_mercado: datos.precio_mercado ?? null,
    p_stock_minimo: datos.stock_minimo ?? null,
    p_stock_critico: datos.stock_critico ?? null,
    p_proveedor_id: datos.proveedor_id || null,
    p_vendible_suelto: typeof datos.vendible_suelto === 'boolean' ? datos.vendible_suelto : null,
    p_notas: datos.notas ?? null,
    p_iva_pct: datos.iva_pct ?? null,
  })
  if (error) throw error
  return data
}

export async function borrarProducto(id) {
  const { data, error } = await supabase.rpc('admin_borrar_producto', { p_id: id })
  if (error) throw error
  return data
}

export async function restaurarProducto(id) {
  const { data, error } = await supabase.rpc('admin_restaurar_producto', { p_id: id })
  if (error) throw error
  return data
}

export async function actualizarProducto(id, campos) {
  const { error } = await supabase
    .from('productos')
    .update(campos)
    .eq('id', id)
  if (error) throw error
}

export async function getProveedores(soloActivos = true) {
  let q = supabase
    .from('proveedores')
    .select('id, nombre, telefono, email, contacto, direccion, horarios, rut, web, forma_pago, categoria, notas, activo, created_at')
    .order('nombre')
  if (soloActivos) q = q.eq('activo', true)
  const { data, error } = await q
  if (error) return []
  return data || []
}

export async function getProveedoresConStats() {
  const { data: provs } = await supabase
    .from('proveedores')
    .select('id, nombre, telefono, email, contacto, direccion, horarios, rut, web, forma_pago, categoria, notas, activo')
    .order('nombre')
  if (!provs) return []
  // Sumamos: cuántos productos por proveedor + valor del inventario
  const { data: prods } = await supabase
    .from('productos')
    .select('proveedor_id, stock_actual, costo_unitario')
    .eq('activo', true)
  const stats = {}
  ;(prods || []).forEach(p => {
    if (!p.proveedor_id) return
    if (!stats[p.proveedor_id]) stats[p.proveedor_id] = { count: 0, valor: 0 }
    stats[p.proveedor_id].count += 1
    stats[p.proveedor_id].valor += Number(p.stock_actual) * Number(p.costo_unitario)
  })
  return provs.map(p => ({
    ...p,
    productos_count: stats[p.id]?.count || 0,
    valor_inventario: stats[p.id]?.valor || 0
  }))
}

export async function crearProveedor(datos) {
  const { data, error } = await supabase.from('proveedores').insert(datos).select().single()
  if (error) throw error
  return data
}

export async function actualizarProveedor(id, datos) {
  const { error } = await supabase.from('proveedores').update(datos).eq('id', id)
  if (error) throw error
}

export async function getDashboardStock() {
  const { data, error } = await supabase.rpc('dashboard_stock')
  if (error) { console.error(error); return null }
  return data?.[0] || null
}

export async function getForecast() {
  const { data, error } = await supabase
    .from('v_stock_forecast')
    .select('*')
    .order('estado_stock')
    .order('sku')
  if (error) { console.error(error); return [] }
  return data || []
}

export async function registrarCompra({ productoId, cantidad, costoUnitario, proveedorId, factura, notas }) {
  const { data, error } = await supabase.rpc('registrar_compra', {
    producto_id_param: productoId,
    cantidad_param: cantidad,
    costo_unitario_param: costoUnitario,
    proveedor_id_param: proveedorId || null,
    factura_param: factura || null,
    notas_param: notas || null,
  })
  if (error) throw error
  return data
}

export async function getSugerenciaCompra(proveedorId = null, diasCobertura = 7) {
  const { data, error } = await supabase.rpc('sugerencia_compra', {
    proveedor_id_param: proveedorId,
    dias_cobertura: diasCobertura,
  })
  if (error) { console.error(error); return [] }
  return data || []
}

export async function ajustarStock({ productoId, cantidadNueva, motivo }) {
  const { error } = await supabase.rpc('ajustar_stock', {
    producto_id_param: productoId,
    cantidad_nueva: cantidadNueva,
    motivo_param: motivo,
  })
  if (error) throw error
}

export async function getMovimientos(productoId = null, limit = 50) {
  let q = supabase
    .from('movimientos_stock')
    .select(`
      id, tipo, cantidad, costo_unitario, referencia_externa, notas, created_at,
      producto:productos(sku, nombre),
      proveedor:proveedores(nombre),
      entrega:entregas(numero_orden)
    `)
    .order('created_at', { ascending: false })
    .limit(limit)
  if (productoId) q = q.eq('producto_id', productoId)
  const { data, error } = await q
  if (error) return []
  return data || []
}

export const ESTADO_STOCK = {
  ok: { label: 'OK', color: '#10B981', bg: '#D1FAE5' },
  bajo: { label: 'Bajo', color: '#F59E0B', bg: '#FEF3C7' },
  critico: { label: 'Crítico', color: '#EF4444', bg: '#FEE2E2' },
  no_alcanza: { label: 'No alcanza 7d', color: '#7C3AED', bg: '#EDE9FE' },
}
